/**
 * TaskUserStatesModal — Görev bazında kullanıcı durum dökümü.
 *
 * Görev listesindeki "göz" ikonundan açılır. Bir görevin tüm kullanıcılarının
 * ham değer / puan / tier durumunu liste halinde gösterir. Admin "SKS A
 * görevde puan aldı mı, hangi tier'da, eşik altı mı?" sorusunu tek bakışta
 * yanıtlayabilir.
 *
 * Veri: GET /rule-engine/tasks/{id}/user-states
 * Filtre: arama (kullanici_kodu) + durum (all/scored/zero/null)
 * Sıralama: total_points / raw_value / kullanici_kodu
 *
 * Stil: zuzzuu_admin temasıyla uyumlu — Card, Badge, Input, Select, Modal.
 */
import { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import { Icon } from '../../components/ui/Icon';
import { tasksApi } from '../../services/api';
import { downloadBlob } from '../../utils/download';
import UserStateDetailModal from './UserStateDetailModal';

const STATUS_FILTERS = [
  { value: 'all',    label: 'Tümü' },
  { value: 'scored', label: 'Puan Alanlar' },
  { value: 'zero',   label: 'Eşik Altı (raw dolu, puan 0)' },
  { value: 'null',   label: 'Veri Yok (raw NULL)' },
];

const SORTS = [
  { value: 'total_points_desc',  label: 'Puan ↓' },
  { value: 'total_points_asc',   label: 'Puan ↑' },
  { value: 'raw_value_desc',     label: 'Ham Değer ↓' },
  { value: 'raw_value_asc',      label: 'Ham Değer ↑' },
  { value: 'kullanici_kodu_asc', label: 'Kullanıcı Kodu (A→Z)' },
];

const PAGE_SIZE = 50;

export default function TaskUserStatesModal({ open, onClose, task }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');                  // anlık input değeri (UI)
  const [debouncedQ, setDebouncedQ] = useState(''); // 300ms gecikme — fetch buna bağlı
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('total_points_desc');
  // Yenile butonu için counter — her tıklamada artar, fetch useEffect deps'inde.
  // Diğer state değişmediği halde re-fetch tetiklemenin temiz yolu.
  const [refreshKey, setRefreshKey] = useState(0);
  // Detay modalı için — satırdaki "Detay" butonuna tıklanan kullanıcı bilgisi.
  // null = kapalı, dolu obje = açık.
  const [detailUser, setDetailUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError]     = useState(null);
  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, size: PAGE_SIZE });

  // Modal her açıldığında state reset
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setQ('');
    setDebouncedQ('');
    setStatus('all');
    setSort('total_points_desc');
  }, [open, task?.id]);

  // Search debounce — kullanıcı hızlı yazarken backend'e her tuşta istek atma.
  // Görev listesi sayfasıyla (pages/Tasks/index.jsx) aynı pattern: 300ms gecikme.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);  // arama değişince ilk sayfaya dön
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  // Veri çek — filtre / sıra / sayfa değişince (q anlık değil debouncedQ)
  useEffect(() => {
    if (!open || !task?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    tasksApi
      .userStates(task.id, {
        page,
        size: PAGE_SIZE,
        q: debouncedQ || undefined,
        status,
        sort,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setRows(data.rows || []);
        setMeta(data.meta || null);
        setPagination(res.pagination || { total: 0, page, size: PAGE_SIZE });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Kullanıcı durumları yüklenemedi.');
        setRows([]);
        setMeta(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, task?.id, page, debouncedQ, status, sort, refreshKey]);

  // Excel indir — ekrandaki filtre/sıralama ile tutarlı TÜM satırları indirir
  // (sayfalama yok). Backend: GET /user-states/export.
  const handleExport = async () => {
    if (!task?.id || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const res = await tasksApi.exportUserStates(task.id, {
        q: debouncedQ || undefined,
        status,
        sort,
      });
      downloadBlob(res.data ?? res, `gorev_${task.id}_kullanici_durumlari.xlsx`);
    } catch (err) {
      setError(err?.message || 'Excel indirilemedi.');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = useMemo(() => {
    const t = pagination.total || 0;
    return Math.max(1, Math.ceil(t / PAGE_SIZE));
  }, [pagination.total]);

  const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }));

  // Son hesaplama tarihi — "15 May 14:30" şeklinde kompakt format.
  // Backend `meta.last_calculated_at` ISO string olarak gönderiyor; null ise
  // hiç hesaplama yapılmamış demektir.
  const fmtCalcDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('tr-TR', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        // Tek satırda, kompakt başlık — ikon size-4 (16px), görev adı truncate'li
        // overflow durumunda ellipsis yapar. min-w-0 + flex-1 ile düzgün küçülür.
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="users" className="w-4 h-4 text-primary-600 flex-shrink-0" />
          <span className="whitespace-nowrap">Kullanıcı Durumları</span>
          {task?.name && (
            <span
              className="text-sm text-neutral-500 font-normal truncate min-w-0"
              title={task.name}
            >
              — {task.name}
            </span>
          )}
        </div>
      }
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-neutral-500">
            {pagination.total > 0 && (
              <>
                Toplam {pagination.total.toLocaleString('tr-TR')} kayıt · Sayfa {page}/{totalPages}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon="chevron-left"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Önceki
            </Button>
            <Button
              variant="secondary"
              icon="chevron-right"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Sonraki
            </Button>
            <Button variant="primary" onClick={onClose}>Kapat</Button>
          </div>
        </div>
      }
    >
      {/* Son hesaplama tarihi + Yenile butonu — admin "veri ne kadar güncel" görsün
        * ve manuel re-fetch yapabilsin. refreshKey bump → fetch useEffect tetiklenir. */}
      <div className="flex items-center justify-between mb-2 text-xs">
        <div className="text-neutral-500">
          {meta?.last_calculated_at ? (
            <>
              <Icon name="clock" className="inline w-3.5 h-3.5 mr-1 align-text-bottom text-neutral-400" />
              <span>Son hesaplama: </span>
              <span className="font-medium text-neutral-700">
                {fmtCalcDate(meta.last_calculated_at)}
              </span>
            </>
          ) : (
            <span className="italic text-neutral-400">Henüz hesaplama yapılmadı</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700 hover:text-neutral-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Veriyi yeniden çek"
        >
          <Icon
            name="refresh-cw"
            className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
          />
          <span>Yenile</span>
        </button>
      </div>

      {/* Özet kartları */}
      {meta?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <StatCard label="Toplam"        value={meta.stats.toplam_kullanici} />
          <StatCard label="Puan Alan"     value={meta.stats.puanli_kullanici} color="green" />
          <StatCard label="Veri Yok"      value={meta.stats.raw_null_kullanici} color="amber" />
          <StatCard label="Ortalama Puan" value={fmtNum(meta.stats.ortalama_puan)} />
          <StatCard label="Max Puan"      value={fmtNum(meta.stats.max_puan)} color="primary" />
        </div>
      )}

      {/* Tier breakdown — her tier'da kaç kullanıcı */}
      {Array.isArray(meta?.tier_breakdown) && meta.tier_breakdown.length > 0 && (
        <div className="mb-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
          <p className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1">
            <Icon name="bar-chart-2" className="w-3.5 h-3.5" />
            Eşik Başına Geçen Kullanıcı
          </p>
          <div className="flex flex-wrap gap-2">
            {meta.tier_breakdown.map((t) => (
              <div
                key={t.tier_index}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-xs"
              >
                <span className="text-neutral-500">≥ {t.threshold_value}</span>
                <Badge color="primary" size="sm">{t.points} pt</Badge>
                <span className="font-semibold text-neutral-900">{t.user_count}</span>
                <span className="text-neutral-400">kişi</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtre satırı */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Kullanıcı kodu veya ad/soyad ara…"
          className="w-64"
        />
        <Select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          options={STATUS_FILTERS}
          className="w-56"
        />
        <Select
          value={sort}
          onChange={(e) => { setSort(e.target.value); setPage(1); }}
          options={SORTS}
          className="w-48"
        />
        <Button
          variant="secondary"
          icon="download"
          onClick={handleExport}
          disabled={exporting || loading || (pagination.total || 0) === 0}
          title="Bu filtredeki tüm satırları Excel olarak indir"
        >
          {exporting ? 'İndiriliyor…' : 'İndir'}
        </Button>
      </div>

      {/* Hata */}
      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      {/* Tablo */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr className="text-left text-xs font-semibold text-neutral-700 border-b border-neutral-200">
                <th className="px-3 py-2">Kullanıcı</th>
                <th className="px-3 py-2 text-right">Ham Değer</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2 text-right">Puan</th>
                <th className="px-3 py-2 text-right">Bonus</th>
                <th className="px-3 py-2 text-right">Toplam</th>
                <th className="px-3 py-2 text-center w-16">Detay</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">Yükleniyor…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">
                  Filtreye uyan kayıt yok.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.kullanici_kodu}
                  className="border-b border-neutral-100 hover:bg-neutral-50"
                >
                  {/* Kullanıcı kodu + ad soyad — kompakt 2-satır gösterim.
                      Ad soyad kullanici_adsoyad alanından (public.as_user_registry
                      LEFT JOIN — boş gelirse satır kayboltulur). truncate ile
                      taşma önlenir, hover tooltip ile tam ad görünür. */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs">{r.kullanici_kodu}</span>
                      {/* Pasif kullanıcı: leaderboard'a girmez ama görev detayında
                          değerleriyle görünür (müşteri talebi). */}
                      {r.is_active === false && (
                        <Badge color="neutral" size="sm">Pasif</Badge>
                      )}
                    </div>
                    {r.kullanici_adsoyad && (
                      <div
                        className="text-[11px] text-neutral-500 truncate max-w-[180px] mt-0.5"
                        title={r.kullanici_adsoyad}
                      >
                        {r.kullanici_adsoyad}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.raw_value == null
                      ? <span className="text-neutral-300 italic">—</span>
                      : fmtNum(r.raw_value)}
                  </td>
                  <td className="px-3 py-2">
                    {r.tier_label
                      ? <Badge color="blue" size="sm">{r.tier_label}</Badge>
                      : <span className="text-neutral-300 italic text-xs">eşik altı</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.points)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.bonus_points > 0
                      ? <Badge color="green" size="sm">{fmtNum(r.bonus_points)}</Badge>
                      : <span className="text-neutral-400">{fmtNum(r.bonus_points)}</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {fmtNum(r.total_points)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {/* Detay butonu — CH ham satırlarını gösteren modal aç.
                      * Ham değer NULL (RATIO zero-denom) ise gizliyoruz:
                      * gösterilecek bir şey yok. */}
                    {r.raw_value != null && (
                      <button
                        type="button"
                        onClick={() => setDetailUser(r)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
                        title="Hesaplama detayını gör (CH ham veri)"
                      >
                        <Icon name="search" className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CH ham veri detay modali — satırdaki search ikonuyla açılır.
        * detailUser hücreden gelen full row objesi (kullanici_kodu + kullanici_adsoyad). */}
      <UserStateDetailModal
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        task={task}
        user={detailUser}
      />
    </Modal>
  );
}

function StatCard({ label, value, color = 'neutral' }) {
  const colorMap = {
    neutral: 'border-neutral-200 bg-white text-neutral-900',
    green:   'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber:   'border-amber-200 bg-amber-50 text-amber-900',
    primary: 'border-primary-200 bg-primary-50 text-primary-900',
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${colorMap[color]}`}>
      <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-lg font-bold mt-0.5 tabular-nums">{value ?? '—'}</p>
    </div>
  );
}
